<template>
  <div class="shift-calendar">
    <el-calendar v-model="calendarDate">
      <template #date-cell="{ data }">
        <div class="calendar-date-click-area">
          <div class="calendar-popover-trigger-wrapper">
            <template v-if="mode === 'view'">
              <el-popover
                v-if="hasShift(data.date)"
                placement="top"
                trigger="click"
                :width="220"
                :visible-arrow="true">
                <template #reference>
                  <div
                    :class="{ 'has-break': getBreaks(data.date).length > 0 }"
                    class="calendar-popover-trigger"
                    @click.stop="$emit('day-click', data)"
                  >
                    <div class="calendar-date-inner">
                      <div class="cell-date fw-bold small">{{ data.date.getDate() }}</div>
                      <el-tag v-if="getShiftTime(data.date)" type="primary" size="small" class="cell-shift-tag">
                        🕘 {{ getShiftTime(data.date) }}
                      </el-tag>
                      <div class="cell-break-icons" v-if="getBreaks(data.date).length">
                        <span v-for="(br, idx) in getBreaks(data.date)" :key="idx" class="break-icon">
                          <el-tag
                            v-if="br.type === 'lunch'"
                            size="small"
                            type="info"
                            class="cell-break-tag cell-break-tag"
                          >🍽️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'break'"
                            size="small"
                            type="info"
                            class="cell-break-tag cell-break-tag"
                          >⏸️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'maintenance'"
                            size="small"
                            type="info"
                            class="cell-break-tag cell-break-tag"
                          >🛠️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'handover'"
                            size="small"
                            type="info"
                            effect="plain"
                            class="cell-break-tag cell-break-tag"
                          >🔁</el-tag>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>

                <div class="text-start">
                  <div v-if="getShiftTime(data.date)">
                    <strong>Режим:</strong>
                    <div class="mb-2">🕘 {{ getShiftTime(data.date) }}</div>
                  </div>
                  <div v-if="getBreaks(data.date).length">
                    <strong>Перерывы:</strong>
                    <ul class="mb-0 ps-3">
                      <li v-for="(br, idx) in getBreaks(data.date)" :key="idx">
                        <span v-if="br.type === 'lunch'">🍽️</span>
                        <span v-else-if="br.type === 'break'">⏸️</span>
                        <span v-else-if="br.type === 'maintenance'">🛠️</span>
                        <span v-else-if="br.type === 'handover'">🔁</span>
                        {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="!getShiftTime(data.date) && !getBreaks(data.date).length">Нет смен</div>
                </div>
              </el-popover>

              <div v-else class="calendar-popover-trigger">
                <div class="calendar-date-inner">
                  <div class="cell-date fw-bold small">{{ data.date.getDate() }}</div>
                </div>
              </div>
            </template>

            <template v-else-if="mode === 'edit'">
              <el-popover
                v-if="isSelected(data.date)"
                placement="top"
                trigger="manual"
                :width="170"
                :visible="activeDate === formatDate(data.date)"
                :teleported="false"
                @after-leave="activeDate = null"
                :class="{ disabled: !workingHours }">
                <template #reference>
                  <div class="calendar-date-click-area"
                       :class="{ selected: isSelected(data.date), disabled: !workingHours || isPast(data.date) }"
                       @click.stop="onDayClick(data)">
                    <div class="calendar-date-inner">
                      <div class="cell-date fw-bold small">{{ data.date.getDate() }}</div>
                      <el-tag v-if="getShiftTime(data.date)" type="primary" size="small" effect="dark" class="cell-shift-tag">
                        🕘 {{ getShiftTime(data.date) }}
                      </el-tag>
                      <div class="cell-break-icons" v-if="getBreaks(data.date).length">
                        <span v-for="(br, idx) in getBreaks(data.date)" :key="idx" class="break-icon">
                          <el-tag
                            v-if="br.type === 'lunch'"
                            size="small"
                            type="info"
                            effect="plain"
                            class="cell-break-tag cell-break-tag"
                          >🍽️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'break'"
                            size="small"
                            type="info"
                            effect="plain"
                            class="cell-break-tag cell-break-tag"
                          >⏸️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'maintenance'"
                            size="small"
                            type="info"
                            effect="plain"
                            class="cell-break-tag cell-break-tag"
                          >🛠️</el-tag>
                          <el-tag
                            v-else-if="br.type === 'handover'"
                            size="small"
                            type="info"
                            effect="plain"
                            class="cell-break-tag cell-break-tag"
                          >🔁</el-tag>
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
                <div class="text-start">
                  <div style="margin-bottom: 10px"><strong>Смена:</strong> {{ getShiftTime(data.date) }} </div>
                  <div v-if="getBreaks(data.date).length">
                    <strong>Перерывы:</strong>
                    <ul class="mb-0 ps-3 mt-2 mb-3">
                      <li v-for="(br, idx) in getBreaks(data.date)" class="mb-1" :key="idx" style="display:flex;align-items:center;justify-content:space-between;">
                        <span>
                          <span v-if="br.type === 'lunch'">🍽️</span>
                          <span v-else-if="br.type === 'break'">⏸️</span>
                          <span v-else-if="br.type === 'maintenance'">🛠️</span>
                          <span v-else-if="br.type === 'handover'">🔁</span>
                          {{ (br.start_time || br.start)?.slice(0, 5) }}–{{ (br.end_time || br.end)?.slice(0, 5) }}
                        </span>
                        <el-button
                          type="danger"
                          size="small"
                          :icon="Delete"
                          circle
                          @click="removeBreak(activeShift, idx)"
                        />
                      </li>
                    </ul>
                  </div>
                  <div style="display: flex;flex-direction: row;justify-content: center;">
                    <el-tooltip content="Изменить время" placement="top">
                      <el-button size="small" :icon="Edit" type="primary" @click="openEditShift(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Добавить перерыв" placement="top">
                      <el-button size="small" :icon="Plus" type="success" @click="openBreakDialog(activeShift)"/>
                    </el-tooltip>
                    <el-tooltip content="Удалить смену" placement="top">
                      <el-button size="small" :icon="Delete" type="danger" @click="removeShift(formatDate(data.date))"/>
                    </el-tooltip>
                  </div>
                </div>
              </el-popover>
              <div v-else class="calendar-date-click-area"
                   :class="{ disabled: !workingHours || isPast(data.date) }"
                   @click.stop="onDayClick(data)">
                <div class="calendar-date-inner">
                  <div class="cell-date fw-bold small">{{ data.date.getDate() }}</div>
                  <el-tag v-if="getShiftTime(data.date)" type="primary" size="small" effect="dark" class="cell-shift-tag">
                    🕘 {{ getShiftTime(data.date) }}
                  </el-tag>
                  <div class="cell-break-icons" v-if="getBreaks(data.date).length">
                    <span v-for="(br, idx) in getBreaks(data.date)" :key="idx" class="break-icon">
                      <el-tag
                        v-if="br.type === 'lunch'"
                        size="small"
                        type="info"
                        effect="plain"
                        class="cell-break-tag cell-break-tag"
                      >🍽️</el-tag>
                      <el-tag
                        v-else-if="br.type === 'break'"
                        size="small"
                        type="info"
                        effect="plain"
                        class="cell-break-tag cell-break-tag"
                      >⏸️</el-tag>
                      <el-tag
                        v-else-if="br.type === 'maintenance'"
                        size="small"
                        type="info"
                        effect="plain"
                        class="cell-break-tag cell-break-tag"
                      >🛠️</el-tag>
                      <el-tag
                        v-else-if="br.type === 'handover'"
                        size="small"
                        type="info"
                        effect="plain"
                        class="cell-break-tag cell-break-tag"
                      >🔁</el-tag>
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </el-calendar>

    <el-dialog title="Изменить время смены" v-model="editDialogVisible" width="400px">
      <div style="display:flex; gap: 12px;">
        <el-time-select
          v-model="editStart"
          placeholder="Начало"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :max-time="editEnd || maxWorkingHour"
        />
        <el-time-select
          v-model="editEnd"
          placeholder="Конец"
          step="00:15"
          :start="minWorkingHour"
          :end="maxWorkingHour"
          :min-time="editStart || minWorkingHour"
        />
      </div>
      <template #footer>
        <el-button @click="editDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveEditShift">Сохранить</el-button>
      </template>
    </el-dialog>

    <el-dialog title="Добавить перерыв" v-model="breakDialogVisible" width="400px">
      <el-form label-width="120px">
        <el-form-item label="Тип">
          <el-radio-group v-model="breakType">
            <el-radio-button label="lunch">🍽️</el-radio-button>
            <el-radio-button label="break">⏸️</el-radio-button>
            <el-radio-button label="maintenance">🛠️</el-radio-button>
            <el-radio-button label="handover">🔁</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Начало">
          <el-select v-model="breakStart" placeholder="Начало" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'s-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isStartDisabled(opt)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Конец">
          <el-select v-model="breakEnd" placeholder="Конец" filterable style="width: 100%">
            <el-option
              v-for="opt in timeOptions"
              :key="'e-' + opt"
              :label="opt"
              :value="opt"
              :disabled="isEndDisabled(opt)"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="breakDialogVisible = false">Отмена</el-button>
        <el-button type="primary" @click="saveBreak">Сохранить</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Delete, Edit, Plus } from '@element-plus/icons-vue'

export default {
  name: 'ShiftCalendar',
  computed: {
    Delete() {
      return Delete
    },
    Plus() {
      return Plus
    },
    Edit() {
      return Edit
    },
    minWorkingHour() {
      return this.workingHours ? (this.workingHours.start_time || this.workingHours.start) : '00:00'
    },
    maxWorkingHour() {
      return this.workingHours ? (this.workingHours.end_time || this.workingHours.end) : '23:59'
    },
    disabledBreakTimes() {
      if (!this.breakShift?.breaks) return []
      return this.breakShift.breaks.map(b => [b.start, b.end])
    },
    timeOptions() {
      const res = []
      const start = (this.minWorkingHour || '00:00').slice(0,5)
      const end = (this.maxWorkingHour || '23:59').slice(0,5)
      const toMin = t => {
        const [h,m] = t.split(':').map(Number); return h*60+m
      }
      const toStr = m => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`
      let cur = toMin(start)
      const stop = toMin(end)
      while (cur <= stop) { res.push(toStr(cur)); cur += 15 }
      return res
    },
  },
  props: {
    shifts: {
      type: Array,
      required: true,
    },
    mode: {
      type: String,
      default: 'view',
      validator(value) {
        return ['view', 'edit'].includes(value);
      }
    },
    workingHours: {
      type: Object,
      required: false,
      default: null,
    },
    selectedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    userName: { type: String, default: '' }
  },
  emits: ['update:selectedDates', 'update:shifts'],
  data() {
    return {
      calendarDate: new Date(),
      activeShift: null,
      activeDate: null,
      internalShifts: [],
      editDialogVisible: false,
      editShift: null,
      editStart: '',
      editEnd: '',
      breakDialogVisible: false,
      breakShift: null,
      breakType: 'lunch',
      breakStart: '',
      breakEnd: ''
    }
  },
  watch: {
    shifts: {
      immediate: true,
      deep: true,
      handler (val) {
        this.internalShifts = (Array.isArray(val) ? val : []).map(shift => {
          const start = shift.start_time || shift.start
          const end = shift.end_time || shift.end

          return {
            ...shift,
            start_time: start,
            end_time: end,
            start,
            end,
            breaks: Array.isArray(shift.breaks)
              ? shift.breaks.map(b => {
                  const bStart = b.start_time || b.start
                  const bEnd = b.end_time || b.end
                  return {
                    ...b,
                    start: bStart,
                    end: bEnd
                  }
                })
              : []
          }
        })
      }
    }
  },
  mounted() {
  },
  methods: {
    isStartDisabled(t) {
      const time = t.slice(0, 5)
      // запрещаем ставить начало внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time >= s && time < e)) return true
      // запрещаем если конец выбран раньше
      if (this.breakEnd && time >= this.breakEnd) return true
      return false
    },
    isEndDisabled(t) {
      const time = t.slice(0, 5)
      // конец не может быть внутри существующих перерывов
      if ((this.disabledBreakTimes || []).some(([s, e]) => time > s && time <= e)) return true
      // конец не может быть раньше начала
      if (!this.breakStart) return true
      if (time <= this.breakStart) return true
      return false
    },
    formatDate(date) {
      return date.getFullYear() + '-' +
             String(date.getMonth() + 1).padStart(2, '0') + '-' +
             String(date.getDate()).padStart(2, '0');
    },
    hasShift(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    getBreaks(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      return shift && Array.isArray(shift.breaks) ? shift.breaks : [];
    },
    getShiftTime(date) {
      const dStr = this.formatDate(date);
      const shift = this.internalShifts.find(s => s.date === dStr);
      if (shift && shift.start_time && shift.end_time) {
        return `${shift.start_time.slice(0, 5)}–${shift.end_time.slice(0, 5)}`;
      }
      return null;
    },
    isSelected(date) {
      const dStr = this.formatDate(date);
      return this.internalShifts.some(s => s.date === dStr);
    },
    isPast(date) {
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      return dStr < todayStr;
    },
    onDayClick(data) {
      // data: { date: Date, type: string, ... }
      const panelYear = this.calendarDate.getFullYear();
      const panelMonth = this.calendarDate.getMonth();
      if (!this.workingHours) {
        this.$message?.warning?.('Сначала выберите локацию с рабочим режимом')
        return
      }
      const date = data.date;
      const dStr = this.formatDate(date);
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя создавать/редактировать смены в прошлом. Доступно с сегодняшнего дня');
        return;
      }
      const idx = this.internalShifts.findIndex(s => s.date === dStr);
      if (idx === -1) {
        // Первый клик: создаём смену и подсвечиваем день
        const newShift = {
          date: dStr,
          start_time: this.workingHours?.start_time || this.workingHours?.start || '09:00',
          end_time: this.workingHours?.end_time || this.workingHours?.end || '23:59',
          breaks: []
        };
        const newShifts = [...this.internalShifts, newShift];
        this.internalShifts = newShifts;
        this.$emit('update:shifts', newShifts);
        this.activeDate = dStr;
        this.activeShift = newShift;
      } else {
        // Повторный клик: открыть/закрыть поповер управления
        this.activeShift = this.internalShifts[idx];
        this.activeDate = (this.activeDate === dStr) ? null : dStr;
      }
      if (data.type === 'prev-month' || data.type === 'next-month') {
        this.$nextTick(() => {
          this.calendarDate = new Date(panelYear, panelMonth, 1);
        });
      }
    },
    removeShift(dStr) {
      const todayStr = this.formatDate(new Date());
      if (dStr < todayStr) {
        this.$message?.warning?.('Нельзя изменять прошедшие смены');
        return;
      }
      this.$confirm('Удалить эту смену?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const newShifts = this.internalShifts.filter(s => s.date !== dStr)
        this.internalShifts = newShifts
        this.$emit('update:shifts', newShifts)
        this.activeDate = null
      }).catch(() => {})
    },
    openEditShift(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя редактировать прошедшие смены');
        return;
      }
      this.editShift = shift
      this.editStart = shift.start_time
      this.editEnd = shift.end_time
      this.editDialogVisible = true
    },
    saveEditShift() {
      if (!this.editShift) return
      this.editShift.start_time = this.editStart
      this.editShift.end_time = this.editEnd
      this.$emit('update:shifts', this.internalShifts)
      this.editDialogVisible = false
      this.editShift = null
    },
    openBreakDialog(shift) {
      if (this.isPast(new Date(shift.date))) {
        this.$message?.warning?.('Нельзя добавлять перерывы в прошедшие смены');
        return;
      }
      // Всегда берем свежую ссылку из internalShifts
      this.breakShift = this.internalShifts.find(s => s.date === shift.date) || shift
      this.breakType = 'lunch'
      this.breakStart = ''
      this.breakEnd = ''
      this.breakDialogVisible = true
    },
    saveBreak() {
      if (!this.breakShift) return
      if (!this.breakStart || !this.breakEnd) {
        this.$message?.warning?.('Укажите время перерыва')
        return
      }

      // Нормализация времени к HH:MM
      const toHM = (t) => (t || '').slice(0, 5)
      const nStart = toHM(this.breakStart)
      const nEnd = toHM(this.breakEnd)

      // Проверка пересечения с существующими перерывами
      const existing = this.breakShift.breaks || []
      const overlaps = existing.some(b => {
        const startB = toHM(b.start || b.start_time)
        const endB = toHM(b.end || b.end_time)
        return (nStart < endB && nEnd > startB)
      })
      if (overlaps) {
        this.$message?.warning?.('Выбранное время пересекается с существующим перерывом')
        return
      }

      const newBreak = {
        date: this.breakShift.date,
        type: this.breakType,
        start: nStart,
        end: nEnd
      }

      const updatedShifts = this.internalShifts.map(s => {
        if (s.date === this.breakShift.date) {
          return {
            ...s,
            breaks: [...(s.breaks || []), newBreak]
          }
        }
        return s
      })

      this.internalShifts = updatedShifts
      this.$emit('update:shifts', updatedShifts)

      // Обновляем активную смену на свежую ссылку, чтобы следующее добавление видело актуальные перерывы
      this.activeShift = this.internalShifts.find(s => s.date === this.breakShift.date) || null

      this.breakDialogVisible = false
      this.breakShift = null
    },
    removeBreak(shift, idx) {
      this.$confirm('Удалить этот перерыв?', 'Подтверждение', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
      }).then(() => {
        const updatedShifts = this.internalShifts.map(s => {
          if (s.date === shift.date) {
            const newBreaks = [...s.breaks]
            newBreaks.splice(idx, 1)
            return { ...s, breaks: newBreaks }
          }
          return s
        })
        this.internalShifts = updatedShifts
        this.$emit('update:shifts', updatedShifts)
      }).catch(() => {
      })
    }
  }
};
</script>
<style scoped>
.shift-calendar {
  width: 100%;
}
.calendar-cell {
  max-height: 100px;
  overflow-y: auto;
}

.cell-break-time {
  line-height: 1.05;
  font-size: 10px;
}
.cell-shift-time {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
}
.cell-info {
  font-size: 10px;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.calendar-popover-trigger {
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  user-select: none;
}
.calendar-date-click-area.selected {
  background-color: #f0f9eb !important;
  border-radius: 4px;
  color: #000 !important;
}
.calendar-popover-trigger-wrapper {
  width: 100%;
  height: 100%;
}
.calendar-date-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 2px;
}
.cell-date {
  margin-bottom: 2px;
}

/* Убираем стандартный hover эффект у el-calendar на уровне ячейки дня */
::v-deep(.el-calendar-day:hover) {
  background-color: transparent !important;
}

/* Убираем внутренние отступы у ячеек дня */
::v-deep(.el-calendar-day) {
  padding: 0 !important;
  display: flex;
  align-items: stretch;
}

/* Центрируем число и делаем его ровным */
.cell-date {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  font-weight: 500;
}

/* Добавлен margin-bottom для .cell-date выше */

/* Все дни делаем тусклее */
::v-deep(.el-calendar-table td):not(.is-today) {
  color: #999;
}
::v-deep(.el-calendar-table td.is-selected) {
  background-color: transparent;
}

.cell-shift-tag, .cell-break-tag {
  margin-top: 3px;
  font-weight: 600;
  display: flex;
  text-align: center;
  font-size: 9px;
  justify-content: flex-start;
  align-items: center;
}

/* Подсветка выходных дней (суббота и воскресенье) */
::v-deep(.el-calendar-table colgroup col:nth-child(7)),
::v-deep(.el-calendar-table colgroup col:nth-child(6)) {
  background-color: #fff5f5;
}

::v-deep(.el-calendar-table td:nth-child(7) .el-calendar-day),
::v-deep(.el-calendar-table td:nth-child(6) .el-calendar-day) {
  background-color: #fff5f5;
}

::v-deep(.el-calendar-table th) {
  background-color: var(--el-border-color-lighter);
}

::v-deep(.el-calendar-table th:nth-child(7)),
::v-deep(.el-calendar-table th:nth-child(6)) {
  background-color: #fff5f5;
}

::v-deep(.el-popover) {
  max-width: 360px;
  white-space: normal;
}

.calendar-date-click-area.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.cell-break-icons {
  display: flex;
  flex-direction: row;
  gap: 2px;
  margin-top: 2px;
  font-size: 12px;
}
.break-icon {
  line-height: 1;
}

</style>

